import {
    Context,
    createContext,
    FunctionComponent,
    ReactNode,
    RefObject,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react'
import { Daycare } from '@/types/Daycare'
import { getInStorage, setInStorage, useAuthToken } from '../token'
import { DeviceInfo } from '@capacitor/device'
import { useLazyQuery } from '@apollo/client'
import { GET_USER_FROM_TOKEN } from '../API/account/accountHandler'
import { GET_DAYCARE, GET_DAYCARE_LIST } from '../API/daycare/daycareHandler'
import { AuthContextType, ContextUser, DaycareRights } from '@/types/Context'
import { defineDaycareRights, noDaycareRights } from './daycareRights'
import { useTranslation } from 'react-i18next'
import { setUserInterfaceDaycareColors } from '../helper/customization'
import { removeServiceWorker } from '../utils/serviceWorker'

const AuthContext: Context<AuthContextType | undefined> = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: FunctionComponent<{ children: ReactNode }> = ({ children }: { children: ReactNode }) => {
    const { i18n } = useTranslation()

    const [user, setUser] = useState<ContextUser | null>(null)

    const [loadingAuth, setLoadingAuth] = useState<boolean>(true)

    const [authToken, initialized, setAuthToken, removeAuthToken] = useAuthToken()
    const [pushToken, setPushToken] = useState<string | undefined>(undefined)

    const [apiVersion, setApiVersion] = useState<string>('')

    const [device, setDevice] = useState<DeviceInfo | null>(null)

    const justLoggedIn: RefObject<boolean> = useRef<boolean>(false)
    const hasCalledGetUser: RefObject<boolean> = useRef<boolean>(false)

    async function login(userData: ContextUser, token?: string): Promise<void> {
        justLoggedIn.current = true
        await i18n.changeLanguage(userData.language || 'fr')

        if (userData.currentDaycare) {
            const daycareRights: DaycareRights = defineDaycareRights(userData.currentDaycare, userData.role!)
            setUser({ ...userData, currentDaycareRights: daycareRights })
            if (userData.currentDaycare.colors) setUserInterfaceDaycareColors(userData.currentDaycare.colors)
            await setInStorage('currentDaycare', userData.currentDaycare.id.toString())
        } else {
            setUser({ ...userData, currentDaycareRights: noDaycareRights })
        }
        if (token) {
            setAuthToken(token)
        }
        setLoadingAuth(false)
    }

    function logout(): void {
        setUser(null)
        setAuthToken('')
        setPushToken(undefined)
        removeAuthToken()
        hasCalledGetUser.current = false
    }

    const [getDaycareList, { data: dataDaycareList }] = useLazyQuery(GET_DAYCARE_LIST)

    useEffect(() => {
        async function getData(): Promise<void> {
            if (!dataDaycareList) return

            const currentDaycareId: string | undefined = (await getInStorage('currentDaycare')) || undefined

            setUser((prev) => {
                if (!prev) return prev

                if (currentDaycareId) {
                    const daycare: Daycare | undefined = dataDaycareList.getDaycareList.find(
                        (daycare: Daycare): boolean => daycare.id.toString() === currentDaycareId
                    )
                    if (daycare) {
                        const daycareRights: DaycareRights = defineDaycareRights(daycare, prev.role!)
                        if (daycare.colors) setUserInterfaceDaycareColors(daycare.colors)
                        return {
                            ...prev,
                            currentDaycare: daycare,
                            currentDaycareRights: daycareRights,
                            daycares: dataDaycareList.getDaycareList
                        }
                    }
                }

                return { ...prev, daycares: dataDaycareList.getDaycareList }
            })
        }

        getData()
    }, [dataDaycareList])

    async function fetchDaycares(): Promise<void> {
        await getDaycareList()
    }

    const [getDaycare, { data: dataDaycare }] = useLazyQuery(GET_DAYCARE, {
        fetchPolicy: 'network-only',
        variables: {
            daycare_id: user?.currentDaycare?.id
        }
    })

    useEffect(() => {
        if (dataDaycare) {
            setUser((prev) => (prev ? { ...prev, currentDaycare: dataDaycare.getDaycare } : prev))
        }
    }, [dataDaycare])

    async function fetchCurrentDaycare(): Promise<void> {
        if (user && user.currentDaycare) {
            await getDaycare()
            await fetchDaycares()
        }
    }

    function setCurrentDaycare(daycareId: number): void {
        if (user) {
            const daycare: Daycare | undefined = user.daycares.find(
                (daycare: Daycare): boolean => daycare.id === daycareId
            )

            if (daycare) {
                const daycareRights: DaycareRights = defineDaycareRights(daycare, user.role!)
                setUser({ ...user, currentDaycare: daycare, currentDaycareRights: daycareRights })
                if (daycare.colors) setUserInterfaceDaycareColors(daycare.colors)

                setInStorage('currentDaycare', daycareId.toString())
            }
        }
    }

    const [getUserFromToken, { data: dataUserFromToken, error: errorUserFromToken }] = useLazyQuery(GET_USER_FROM_TOKEN)

    useEffect(() => {
        if (dataUserFromToken) {
            login(dataUserFromToken.getContextResponse.user, dataUserFromToken.getContextResponse.token).finally(() => {
                setLoadingAuth(false)
            })
        }
    }, [dataUserFromToken])

    useEffect(() => {
        if (
            errorUserFromToken &&
            errorUserFromToken.message !== 'Failed to fetch' &&
            errorUserFromToken.message !== 'Load failed'
        ) {
            setLoadingAuth(false)
            logout()
            removeServiceWorker()
        }
    }, [errorUserFromToken])

    useEffect(() => {
        if (!initialized || !device) return

        if (justLoggedIn.current) {
            justLoggedIn.current = false
            return
        }

        // Éviter les multiples appels si on a déjà récupéré l'utilisateur
        if (hasCalledGetUser.current && user) {
            return
        }

        let cancelled: boolean = false

        setLoadingAuth(true)

        const restoreUser: () => Promise<void> = async (): Promise<void> => {
            if (!user && authToken) {
                hasCalledGetUser.current = true
                const daycareId: string = await getInStorage('currentDaycare')
                if (!cancelled) {
                    await getUserFromToken({
                        variables: {
                            daycareId: daycareId ? Number(daycareId) : undefined,
                            pushToken: pushToken ? pushToken : undefined,
                            deviceType: device.platform ? device.platform : undefined
                        }
                    })
                }
            } else {
                if (!cancelled) setLoadingAuth(false)
            }
        }

        restoreUser()

        return () => {
            cancelled = true
        }
    }, [authToken, initialized, device, pushToken])

    return (
        <AuthContext.Provider
            value={{
                user,
                authToken,
                device,
                pushToken,
                apiVersion,
                loadingAuth,
                login,
                logout,
                setDevice,
                setPushToken,
                fetchDaycares,
                fetchCurrentDaycare,
                setCurrentDaycare,
                setApiVersion
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

// TODO: extraire dans un hook useDaycareRights() (wrapper autour de useAuth() retournant
// DaycareRights avec fallback noDaycareRights) pour éviter de dupliquer les lectures de
// user.currentDaycareRights.* — chantier à part, voir discussion sur canWriteParamsDaycare
// const canUpdate: boolean = !!user?.currentDaycareRights.canWriteParamsDaycare
export const useAuth: () => AuthContextType = (): AuthContextType => {
    const context: AuthContextType | undefined = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
